/**
 * Gate I8 Phase 6: Workflow Board View
 * 
 * Kanban-style board view for the Workflow Dashboard.
 * Columns = Eval Status (4): INCOMPLETE, NEEDS_REVIEW, NEEDS_RECS, COMPLETE
 * Within each column, animals are grouped by Track (5) in fixed order.
 * 
 * Read-only; no drag-and-drop in this phase.
 */

import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WorkflowAnimalTile } from "./WorkflowAnimalTile";
import type { 
  WorkflowDashboardItem, 
  Track, 
  EvalStatus 
} from "@/lib/api/workflow-types";
import { 
  VALID_EVAL_STATUSES, 
  VALID_TRACKS, 
  EVAL_STATUS_LABELS, 
  TRACK_LABELS 
} from "@/lib/api/workflow-types";

interface WorkflowBoardViewProps {
  items: WorkflowDashboardItem[];
  highlightedAnimalId?: string | null;
}

type BoardGrouping = Record<EvalStatus, Record<Track, WorkflowDashboardItem[]>>;

const EVAL_STATUS_STAFF_LABELS: Record<EvalStatus, string> = {
  'INCOMPLETE': 'Incomplete',
  'NEEDS_REVIEW': 'Needs review',
  'NEEDS_RECS': 'Needs recs',
  'COMPLETE': 'Complete'
};

export function WorkflowBoardView({ items, highlightedAnimalId }: WorkflowBoardViewProps) {
  const { grouping, columnCounts } = useMemo(() => {
    const grouped: BoardGrouping = {} as BoardGrouping;
    const counts: Record<EvalStatus, number> = {} as Record<EvalStatus, number>;
    
    for (const status of VALID_EVAL_STATUSES) {
      grouped[status] = {} as Record<Track, WorkflowDashboardItem[]>;
      counts[status] = 0;
      for (const track of VALID_TRACKS) {
        grouped[status][track] = [];
      }
    }
    
    for (const item of items) {
      if (grouped[item.evalStatus] && grouped[item.evalStatus][item.track]) {
        grouped[item.evalStatus][item.track].push(item);
        counts[item.evalStatus]++;
      }
    }
    
    return { grouping: grouped, columnCounts: counts };
  }, [items]);

  return (
    <div 
      className="flex gap-4 overflow-x-auto pb-4"
      data-testid="workflow-board"
    >
      {VALID_EVAL_STATUSES.map((evalStatus) => (
        <div 
          key={evalStatus}
          className="flex-shrink-0 w-80"
          data-testid={`workflow-board-col-${evalStatus}`}
        >
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">
                {EVAL_STATUS_STAFF_LABELS[evalStatus]}
              </h3>
              <span 
                key={`count-${evalStatus}-${columnCounts[evalStatus]}`}
                className="text-xs text-muted-foreground bg-background px-2 py-0.5 rounded-full"
              >
                {columnCounts[evalStatus]}
              </span>
            </div>
            
            <ScrollArea className="h-[calc(100vh-320px)] pr-2">
              <div className="space-y-4">
                {VALID_TRACKS.map((track) => {
                  const trackItems = grouping[evalStatus][track];
                  
                  return (
                    <div 
                      key={track}
                      data-testid={`workflow-board-group-${evalStatus}-${track}`}
                    >
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          {TRACK_LABELS[track]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {trackItems.length}
                        </span>
                      </div>
                      {trackItems.length === 0 ? (
                        <div className="border border-dashed border-muted-foreground/20 rounded-md p-3 bg-muted/30">
                          <p className="text-xs text-muted-foreground/50 text-center italic">
                            No animals in this category
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {trackItems.map((item) => (
                            <div
                              key={item.animalId}
                              id={`workflow-board-animal-${item.animalId}`}
                              data-testid={highlightedAnimalId === item.animalId ? 'workflow-search-highlighted' : undefined}
                            >
                              <WorkflowAnimalTile 
                                item={item}
                                isHighlighted={highlightedAnimalId === item.animalId}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      ))}
    </div>
  );
}

export default WorkflowBoardView;
